import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, ShieldCheck, ArrowRight, KeyRound, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function PartnerLoginModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  if (!open) return null;

  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      setErrorMsg("Please enter your email address.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await api.post("/auth/request-otp", { email: email.trim().toLowerCase() });
      if (res.data.ok) {
        setOtpCode("");
        setStep("otp");
        toast.success(`Login passcode sent to ${email}. Check your email inbox.`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to request login code. Please try again.";
      setErrorMsg(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (!otpCode.trim() || otpCode.trim().length !== 6) {
      setErrorMsg("Please enter the complete 6-digit passcode sent to your email.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await api.post("/auth/verify-otp", { email: email.trim().toLowerCase(), code: otpCode.trim() });
      if (res.data.ok && res.data.user) {
        if (res.data.token) {
          localStorage.setItem("session_token", res.data.token);
        }
        setUser(res.data.user);
        toast.success("Welcome back, Partner!");
        onClose();
        navigate("/app/dashboard", { state: { user: res.data.user } });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid or expired passcode.";
      setErrorMsg(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep("email");
    setEmail("");
    setOtpCode("");
    setErrorMsg("");
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[110] bg-[#0A1128]/85 backdrop-blur-md flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="w-full max-w-md bg-[#0A1128] text-white rounded-xl border border-[#C5A880]/40 shadow-2xl overflow-hidden relative"
        >
          {/* Close button */}
          <button
            onClick={resetModal}
            className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="p-6 lg:p-8 bg-gradient-to-b from-[#152243]/80 to-[#0A1128] border-b border-[#C5A880]/20">
            <div className="flex items-center gap-2.5 text-[#C5A880] mb-2 text-xs font-semibold tracking-widest uppercase">
              <ShieldCheck className="w-4 h-4 text-[#C5A880]" /> Partner Portal
            </div>
            <h3 className="font-serif-display text-2xl text-white font-medium">
              Alpine Straits Sign-In
            </h3>
            <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">
              Authorized partner portal for Alpine Straits Property Partners and accredited intermediaries.
            </p>
          </div>

          {/* Body */}
          <div className="p-6 lg:p-8">
            {errorMsg && (
              <div className="mb-5 p-3.5 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-xs flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}

            {step === "email" ? (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    Authorized Partner Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full bg-[#152243] border border-slate-700 focus:border-[#C5A880] rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none transition-colors"
                      autoFocus
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C5A880] text-[#0A1128] font-semibold hover:bg-[#d6b78e] rounded-lg h-11 transition-colors mt-2"
                >
                  {loading ? "Sending Passcode…" : "Send Login Passcode"} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="p-3.5 bg-[#C5A880]/10 border border-[#C5A880]/30 rounded-lg text-xs text-[#C5A880] flex items-center gap-2.5">
                  <KeyRound className="w-4 h-4 shrink-0 text-[#C5A880]" />
                  <span>Passcode sent to <strong>{email}</strong> via Hostinger SMTP. Check your email inbox.</span>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    Enter 6-Digit Passcode
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="w-full bg-[#152243] border border-[#C5A880]/60 focus:border-[#C5A880] rounded-lg px-4 py-2.5 text-center tracking-[8px] font-mono text-xl text-white placeholder-slate-600 outline-none transition-colors"
                    autoFocus
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C5A880] text-[#0A1128] font-semibold hover:bg-[#d6b78e] rounded-lg h-11 transition-colors"
                >
                  {loading ? "Verifying…" : "Verify & Access Partner Dashboard"} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <button
                  type="button"
                  onClick={() => { setStep("email"); setOtpCode(""); setErrorMsg(""); }}
                  className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition-colors pt-2"
                >
                  ← Change email address
                </button>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 lg:px-8 py-3 bg-[#060A18] border-t border-slate-800 text-[10px] text-slate-500 flex items-center justify-between">
            <span>Alpine Straits Property Partners</span>
            <span>Secured Session</span>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
